import React, { useState, useEffect, useRef } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  YouTube as YouTubeIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  FolderSharedOutlined as FolderSharedOutlinedIcon,
} from "@mui/icons-material";
import { ChevronDown } from "lucide-react";

// Tab Components
import CardProjectDetailVideos from "./CardProjectDetailVideos";
import CardProjectDetailPlaylist from "./CardProjectDetailPlaylist";

// Common
import MeGenBreadcrumbs, { BreadcrumbItem } from "./common/MeGenBreadcrumbs";

// Types
import { RUNS_SLICE_INITIAL, RunsFilter, RunsSlice } from "./common/runsQuery";
import { SeriesSerializer, SeriesType, ShareRunMapData, YouTubeStatusResp } from "./types/MeGenTypes";

// Styles
import { DividerBox, ProjectContent, TabBar, TabButton, TabContent } from "./styles/ProjectDetail";

import CONFIG from "config/config";

// ─── Styled ───────────────────────────────────────────────

const HeaderContainer = styled(Box)({
  marginBottom: "0.5rem",
  flexShrink: 0,
});

const TitleRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "1rem",
});


const ConnectYoutubeButton = styled(Button)({
  backgroundColor: "#e91e1e",
  color: "white",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "0.875rem",
  padding: "8px 16px",
  borderRadius: "4rem",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#bd0000", boxShadow: "none" },
});

const TabBadge = styled("span")({
  fontSize: "0.65rem",
  fontWeight: 600,
  backgroundColor: "#f1f5f9",
  color: "#94a3b8",
  padding: "1px 6px",
  borderRadius: "9999px",
  marginLeft: "6px",
});

// ─── Props ────────────────────────────────────────────────

export type CardProjectDetailProps = {
  projectId: number;
  projectName: string;
  onNavigateBack?: () => void;
  seriesList?: SeriesSerializer[];
  /** ผลค้น/แบ่งหน้าของแท็บ Videos ที่มาจาก server */
  runs?: RunsSlice;
  /** จำนวน run ทั้งโปรเจกต์จาก server (ไม่ใช่ `runs.items.length` ที่คือ "เท่าที่โหลดมา") */
  runsTotal?: number;
  onSearchRuns?: (filter: RunsFilter, mode?: "replace" | "append") => void;
  /** ผลค้น/แบ่งหน้าของแผง episode ในแท็บ Playlist (`seriesId` ใน slice = series ที่เปิดแผงอยู่) */
  runsPlaylist?: RunsSlice;
  onSearchPlaylistRuns?: (filter: RunsFilter, mode?: "replace" | "append") => void;
  defaultTab?: "videos" | "playlist";
  isRefreshing?: boolean;
  ownerName?: string;
  // Series Section
  onCreateFromScript?: () => void;
  onCreateFromWebsite?: () => void;
  onViewSeries?: (series: SeriesSerializer) => void;
  onEditSeries?: (series: SeriesSerializer) => void;
  onDeleteSeries?: (series: SeriesSerializer) => void;
  onShareSeries?: (series: SeriesSerializer) => void;
  // Runs Section
  onCreateVdoFromWebsite?: (seriesId?: number) => void;
  onCreateVdoFromScript?: (seriesId?: number) => void;
  onCreateVdoFromStory?: (seriesId?: number) => void;
  onSelectEpisode?: (episodeId: string, runType?: SeriesType) => void;
  onRestartEpisode?: (episodeId: string) => void;
  onDuplicateEpisode?: (episodeId: string) => void;
  onDeleteEpisode?: (episodeId: string) => void;
  onShareEpisode?: (shareData: ShareRunMapData) => void;
  // Connection
  onConnectYouTube?: () => void;
  onDeactivateYouTube?: (channelId: string) => void;
  youtubeStatus?: YouTubeStatusResp;
};

// ─── Component ────────────────────────────────────────────

const CardProjectDetail: React.FC<CardProjectDetailProps> = (props) => {
  const seriesList = props.seriesList ?? [];
  const defaultTab = props.defaultTab ?? "videos";

  const [activeTab, setActiveTab] = useState<"videos" | "playlist">(defaultTab);
  const [showYouTubeDropdown, setShowYouTubeDropdown] = useState(false);
  const [isJustUpdated, setIsJustUpdated] = useState(false);
  const wasRefreshing = useRef(false);

  // Trigger fade-in animation when refresh completes
  useEffect(() => {
    if (wasRefreshing.current && !props.isRefreshing) {
      setIsJustUpdated(true);
      const timer = setTimeout(() => setIsJustUpdated(false), 500);
      return () => clearTimeout(timer);
    }
    wasRefreshing.current = props.isRefreshing ?? false;
  }, [props.isRefreshing]);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: props.projectName },
  ];

  const youTubeDropdownRef = React.useRef<HTMLDivElement>(null);

  // Reset tab when project changes
  React.useEffect(() => {
    setActiveTab(defaultTab);
  }, [props.projectId]);

  // Click outside YouTube dropdown
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showYouTubeDropdown &&
        youTubeDropdownRef.current &&
        !youTubeDropdownRef.current.contains(e.target as Node)
      ) {
        setShowYouTubeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showYouTubeDropdown]);

  return (
    <Box id="megen-project-detail-root" sx={{ padding: "0 10px", display: "flex", flexDirection: "column", height: "calc(90vh - 20px)" }}>

      {/* ── Header ── */}
      <HeaderContainer id="megen-project-detail-header">
        <TitleRow id="megen-project-detail-title-row">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, "& > *:first-of-type": { mb: "0 !important", pb: 0 } }}>
            <MeGenBreadcrumbs items={breadcrumbItems} onNavigate={() => props.onNavigateBack?.()} />
            {props.isRefreshing && (
              <CircularProgress size={14} thickness={5} sx={{ color: "#94a3b8", flexShrink: 0 }} />
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            {/* Owner Project */}
            {/* {props.ownerName && (
              <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.25,
                py: 0.5,
                backgroundColor: "#fdf2f8",
                border: "1px solid #f9a8d4",
                borderRadius: "9999px",
              }}>
                <FolderSharedOutlinedIcon sx={{ fontSize: "0.875rem", color: "#db2777" }} />
                <Typography sx={{ fontSize: "0.8125rem", color: "#db2777", fontWeight: 500 }}>
                  Shared by {props.ownerName}
                </Typography>
              </Box>
            )} */}

            {/* YouTube Button */}
            {props.youtubeStatus && props.youtubeStatus.channels?.length > 0 ? (
              <Box id="megen-project-detail-youtube-dropdown-wrapper" ref={youTubeDropdownRef} sx={{ position: "relative" }}>
                <Button
                  id="megen-project-detail-youtube-btn"
                  onClick={() => setShowYouTubeDropdown((prev) => !prev)}
                  startIcon={<YouTubeIcon sx={{ color: "#e91e1e" }} />}
                  endIcon={<ChevronDown size={16} />}
                  sx={{
                    color: "#475569",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    border: "1px solid #e2e8f0",
                    borderRadius: "0.5rem",
                    padding: "6px 12px",
                    "&:hover": { backgroundColor: "#f8fafc" },
                  }}
                >
                  {props.youtubeStatus.channels.length === 1
                    ? props.youtubeStatus.channels[0].channel_name || "Unknown Channel"
                    : `YouTube (${props.youtubeStatus.channels.length})`}
                </Button>

                {showYouTubeDropdown && (
                  <Box
                    id="megen-project-detail-youtube-dropdown"
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: "calc(100% + 4px)",
                      backgroundColor: "white",
                      border: "1px solid #e2e8f0",
                      borderRadius: "0.75rem",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                      minWidth: "220px",
                      zIndex: 20,
                      overflow: "hidden",
                    }}
                  >
                    {/* Connected Channels */}
                    <Box id="megen-project-detail-youtube-channels-section" sx={{ p: 1, borderBottom: "1px solid #f1f5f9" }}>
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        Connected Channels
                      </Typography>
                      {props.youtubeStatus.channels.map((ch) => (
                        <Box
                          id={`megen-project-detail-youtube-channel-${ch.id}`}
                          key={ch.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            borderRadius: "0.5rem",
                            "&:hover .remove-btn": { display: "flex" },
                            "&:hover .status-label": { display: "none" },
                          }}
                        >
                          {ch.status === "connected" ? (
                            <CheckCircleIcon sx={{ fontSize: "0.9rem", color: "#22c55e", flexShrink: 0 }} />
                          ) : (
                            <ErrorIcon sx={{ fontSize: "0.9rem", color: "#f59e0b", flexShrink: 0 }} />
                          )}
                          <Typography sx={{ fontSize: "0.8rem", color: "#334155", flex: 1 }} noWrap>
                            {ch.channel_name || "Unknown Channel"}
                          </Typography>
                          <Typography
                            className="status-label"
                            sx={{ fontSize: "0.7rem", color: ch.status === "connected" ? "#22c55e" : "#f59e0b" }}
                          >
                            {ch.status}
                          </Typography>
                          <Box
                            component="span"
                            className="remove-btn"
                            onClick={() => props.onDeactivateYouTube?.(ch.id!)}
                            sx={{
                              display: "none",
                              alignItems: "center",
                              justifyContent: "center",
                              p: 0.25,
                              borderRadius: "0.25rem",
                              cursor: "pointer",
                              color: "#ef4444",
                              "&:hover": { backgroundColor: "#fef2f2" },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: "0.85rem" }} />
                          </Box>
                        </Box>
                      ))}
                    </Box>

                    {/* Add Channel */}
                    <Box id="megen-project-detail-youtube-add-channel-section" sx={{ p: 1 }}>
                      <Box
                        id="megen-project-detail-youtube-add-channel"
                        onClick={() => {
                          setShowYouTubeDropdown(false);
                          props.onConnectYouTube?.();
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1.5,
                          py: 1,
                          borderRadius: "0.5rem",
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "#eff6ff" },
                        }}
                      >
                        <AddIcon sx={{ fontSize: "1rem", color: "#3b82f6" }} />
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: "#3b82f6" }}>
                          Add Channel
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            ) : 
              CONFIG.MEGEN_USE_DOWNLOAD_URL_BTN ? null : <ConnectYoutubeButton id="megen-project-detail-connect-youtube-btn" startIcon={<YouTubeIcon />} onClick={() => props.onConnectYouTube?.()}>
                Connect YouTube
              </ConnectYoutubeButton>
            }
          </Box>
        </TitleRow>

        <DividerBox id="megen-project-detail-divider" />

        {/* ── Tab Bar ── */}
        <TabBar id="megen-project-detail-tab-bar">
          <TabButton id="megen-project-detail-tab-videos" active={activeTab === "videos"} onClick={() => setActiveTab("videos")}>
            Videos
            <TabBadge>{props.runsTotal ?? 0}</TabBadge>
          </TabButton>
          <TabButton id="megen-project-detail-tab-playlist" active={activeTab === "playlist"} onClick={() => setActiveTab("playlist")}>
            Playlist
            <TabBadge>{seriesList.length}</TabBadge>
          </TabButton>
        </TabBar>
      </HeaderContainer>

      {/* ── Tab Content ── */}
      <ProjectContent id="megen-project-detail-content">
        <TabContent id="megen-project-detail-tab-content">
          {activeTab === "videos" && (
            <CardProjectDetailVideos
              runs={props.runs ?? RUNS_SLICE_INITIAL}
              onSearchRuns={props.onSearchRuns}
              seriesList={seriesList}
              isRefreshing={props.isRefreshing}
              isJustUpdated={isJustUpdated}
              onCreateVdoFromScript={props.onCreateVdoFromScript}
              onSelectEpisode={props.onSelectEpisode}
              onRestartEpisode={props.onRestartEpisode}
              onDuplicateEpisode={props.onDuplicateEpisode}
              onDeleteEpisode={props.onDeleteEpisode}
              onShareEpisode={props.onShareEpisode}
            />
          )}

          {activeTab === "playlist" && (
            <CardProjectDetailPlaylist
              seriesList={seriesList}
              runsPlaylist={props.runsPlaylist ?? RUNS_SLICE_INITIAL}
              onSearchPlaylistRuns={props.onSearchPlaylistRuns}
              onCreateFromScript={props.onCreateFromScript}
              onCreateFromWebsite={props.onCreateFromWebsite}
              onViewSeries={props.onViewSeries}
              onEditSeries={props.onEditSeries}
              onDeleteSeries={props.onDeleteSeries}
              onShareSeries={props.onShareSeries}
              onCreateVdoFromWebsite={props.onCreateVdoFromWebsite}
              onCreateVdoFromScript={props.onCreateVdoFromScript}
              onCreateVdoFromStory={props.onCreateVdoFromStory}
              onSelectEpisode={props.onSelectEpisode}
              onRestartEpisode={props.onRestartEpisode}
              onDuplicateEpisode={props.onDuplicateEpisode}
              onDeleteEpisode={props.onDeleteEpisode}
              onShareEpisode={props.onShareEpisode}
            />
          )}
        </TabContent>
      </ProjectContent>
    </Box>
  );
};

export default CardProjectDetail;
